import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Card, CardBody } from "reactstrap";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import styles from "../../../assets/css/Card.module.css";
import "./clientUsers.css";

interface SendInviteModalProps {
  isOpen: boolean;
  toggle: () => void;
  agencies: any[];
  onSelectAgencyForInvite: (
    id: string | number,
    inviteduser: any,
    userId?: string | number | undefined
  ) => void;
  userId: number;
  inviteduser: any[];
}

// Custom arrow components
const CustomLeftArrow = ({ onClick, carouselState }: any) => {
  const isDisabled = carouselState?.currentSlide === 0;
  return (
    <button
      className="custom-arrow custom-left-arrow"
      onClick={onClick}
      aria-label="Previous"
      disabled={isDisabled}
      style={{ display: 'flex' }}
    >
      <i className="mdi mdi-chevron-left"></i>
    </button>
  );
};

const CustomRightArrow = ({ onClick, carouselState }: any) => {
  const totalItems = carouselState?.totalItems || 0;
  const currentSlide = carouselState?.currentSlide || 0;
  const slidesToShow = carouselState?.slidesToShow || 2;
  const isDisabled = currentSlide + slidesToShow >= totalItems;

  return (
    <button
      className="custom-arrow custom-right-arrow"
      onClick={onClick}
      aria-label="Next"
      disabled={isDisabled}
      style={{ display: 'flex' }}
    >
      <i className="mdi mdi-chevron-right"></i>
    </button>
  );
};

const SendInviteModal: React.FC<SendInviteModalProps> = ({
  isOpen,
  toggle,
  agencies,
  onSelectAgencyForInvite,
  userId,
  inviteduser,
}) => {
  const [maxCardHeight, setMaxCardHeight] = useState(100); // Default height
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    if (agencies.length > 0) {
      setTimeout(() => {
        const cardHeights = Array.from(document.querySelectorAll(".agency-card")).map(
          (card) => (card as HTMLElement).offsetHeight
        );
        setMaxCardHeight(Math.max(...cardHeights));
      }, 100);
    }
  }, [agencies, isOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const responsive = {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 2, slidesToSlide: 2 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 1, slidesToSlide: 1 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1, slidesToSlide: 1 },
  };

  // Conditionally render arrows based on agency count and screen size
  // Desktop (2 items): show arrows only if 3+ agencies
  // Mobile/Tablet (1 item): show arrows if 1+ agencies
  const shouldShowArrows = isMobile ? agencies.length >= 1 : agencies.length > 2;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Send Invite with Below Agencies</ModalHeader>
      <ModalBody style={{ overflow: 'hidden' }}>
        {agencies.length === 1 ? (
          // If only one agency, center it
          <div className="d-flex justify-content-center">
            <Card
              className={`agency-card ${styles.customCard} d-flex flex-row align-items-center`}
              style={{
                width: "350px",
                height: `${maxCardHeight}px`,
                cursor: "pointer",
                padding: "10px",
              }}
              onClick={() => onSelectAgencyForInvite(agencies[0].account_id, inviteduser, userId)}
            >
              <img
                className="img-fluid"
                src={agencies[0].agency_logo}
                alt={agencies[0].agency_name}
                style={{ width: "80px", height: "80px", objectFit: "contain", marginRight: "15px" }}
              />
              <CardBody className="p-0">
                <h5 className="mb-0">{agencies[0].agency_name}</h5>
              </CardBody>
            </Card>
          </div>
        ) : (
          // Use carousel if more than one agency
          <div
            className={`send-invite-carousel-container ${!shouldShowArrows ? 'no-arrows' : ''}`}
            data-agency-count={agencies.length}
          >
            <Carousel
              swipeable={true}
              draggable={true}
              showDots={true}
              responsive={responsive}
              ssr={true}
              infinite={false}
              autoPlaySpeed={2000}
              keyBoardControl={true}
              customTransition="transform 500ms ease-in-out"
              transitionDuration={500}
              containerClass="carousel-container"
              customLeftArrow={shouldShowArrows ? <CustomLeftArrow /> : undefined}
              customRightArrow={shouldShowArrows ? <CustomRightArrow /> : undefined}
              arrows={shouldShowArrows}
              renderArrowsWhenDisabled={shouldShowArrows}
              renderButtonGroupOutside={false}
              renderDotsOutside={true}
              dotListClass="custom-dot-list-style"
              itemClass="carousel-item-padding-40-px"
            >
              {agencies.map((agency) => (
                <div key={agency.id} className="p-2">
                  <Card
                    className={`agency-card ${styles.customCard} d-flex flex-row align-items-center`}
                    style={{
                      height: `${maxCardHeight}px`,
                      cursor: "pointer",
                      padding: "10px",
                    }}
                    onClick={() => onSelectAgencyForInvite(agency.account_id, inviteduser, userId)}
                  >
                    <img
                      className="img-fluid"
                      src={agency.agency_logo}
                      alt={agency.agency_name}
                      style={{ width: "80px", height: "80px", objectFit: "contain", marginRight: "15px", flexShrink: 0 }}
                    />
                    <CardBody className="p-0" style={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center" }}>
                      <h5 className="mb-0" style={{ wordWrap: "break-word", overflowWrap: "break-word", lineHeight: "1.3" }}>{agency.agency_name}</h5>
                    </CardBody>
                  </Card>
                </div>
              ))}
            </Carousel>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default SendInviteModal;
